import React, { useMemo } from 'react'
import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react'
import { formatCurrency, getCurrencySymbol } from './formatters'
import type { SelectedSnapshot } from '../../../utils/overdueInvoiceAging'

type NonNullSnapshot = Exclude<SelectedSnapshot, null>

export type KpiViewMode = 'overdue' | 'invoicing' | 'outstanding'

export type ClientSortKey =
	| 'client_name'
	| 'display_amount'
	| 'base_amount'
	| 'overdue_count'
	| 'total_invoices'
	| 'paid_invoices'
	| 'outstanding_invoices'
	| 'payment_rate'
	| 'avg_days_overdue'
	| 'deposit_amount'
	| 'deposit_adjustment'
	| 'net_deposit_balance'

export interface ClientDetailsTableProps {
	clients: Record<string, unknown>[]
	mode: KpiViewMode
	onModeChange: (m: KpiViewMode) => void
	snapshot: SelectedSnapshot | null
	effectiveOverdueInvoices: number
	effectiveOverdueAmount: number
	clientsWithOverdue: number
	showInvoicingView: boolean
	showOnlyOverdueClients: boolean
	showOutstandingOnly: boolean
	showMultipleOverdueOnly: boolean
	onMultipleOverdueOnlyChange: (v: boolean) => void
	sort: { by: ClientSortKey; dir: 'asc' | 'desc' }
	onSort: (key: ClientSortKey) => void
	onClientClick: (contactId: string | number, name: string) => void
}

const VIEW_TABS: { id: KpiViewMode; label: string }[] = [
	{ id: 'overdue', label: 'Overdue' },
	{ id: 'invoicing', label: 'Invoicing' },
	{ id: 'outstanding', label: 'Outstanding' },
]

function getSortValue(client: Record<string, unknown>, key: ClientSortKey, mode: KpiViewMode): number | string {
	const showInvoicing = mode === 'invoicing'
	const showOutstanding = mode === 'outstanding'
	switch (key) {
		case 'client_name':
			return String(client.contact_name || client.client_name || '').toLowerCase()
		case 'display_amount':
			return showInvoicing
				? Number(client.total_invoiced_amount || client.total_amount || 0)
				: showOutstanding
					? Number(client.total_outstanding_amount || client.outstanding_amount || 0)
					: Number(client.overdue_amount || 0)
		case 'base_amount':
			return showInvoicing
				? Number(client.total_invoiced_amount || client.total_amount || 0)
				: showOutstanding
					? Number(
							client.total_outstanding_amount_original ||
								client.outstanding_amount_original ||
								client.total_outstanding_amount ||
								0
						)
					: Number(client.overdue_amount_original || 0)
		case 'overdue_count':
			return Number(client.overdue_invoice_count || client.overdue_invoices || 0)
		case 'total_invoices':
			return Number(client.total_invoices || 0)
		case 'paid_invoices':
			return Number(client.paid_invoices || 0)
		case 'outstanding_invoices':
			return Number(client.outstanding_invoices || 0)
		case 'payment_rate':
			return Number(client.payment_rate || 0)
		case 'avg_days_overdue':
			return Number(client.avg_days_overdue || 0)
		case 'deposit_amount':
			return Number(client.deposit_amount || 0)
		case 'deposit_adjustment':
			return Number(client.deposit_adjustment || 0)
		case 'net_deposit_balance':
			return Number(client.net_deposit_balance || 0)
		default:
			return 0
	}
}

const ThSort: React.FC<{
	field: ClientSortKey
	label: React.ReactNode
	sort: { by: ClientSortKey; dir: 'asc' | 'desc' }
	onSort: (k: ClientSortKey) => void
	align?: 'left' | 'right'
}> = ({ field, label, sort, onSort, align = 'left' }) => {
	const active = sort.by === field
	return (
		<th
			className={`px-2 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500 cursor-pointer select-none whitespace-nowrap ${
				align === 'right' ? 'text-right' : 'text-left'
			}`}
			onClick={() => onSort(field)}
		>
			<span
				className={`inline-flex items-center gap-0.5 ${align === 'right' ? 'justify-end w-full' : ''}`}
			>
				{label}
				{active ? (
					sort.dir === 'asc' ? (
						<ChevronUp className="w-3 h-3 text-blue-600" />
					) : (
						<ChevronDown className="w-3 h-3 text-blue-600" />
					)
				) : (
					<ChevronsUpDown className="w-3 h-3 text-slate-300" />
				)}
			</span>
		</th>
	)
}

const FilterChip: React.FC<{
	active: boolean
	label: string
	onClick: () => void
}> = ({ active, label, onClick }) => (
	<button
		type="button"
		onClick={onClick}
		className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${
			active
				? 'bg-blue-50 border-blue-300 text-blue-900 shadow-sm'
				: 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300'
		}`}
	>
		{label}
	</button>
)

function ModeSummaryStrip({
	mode,
	snapshot,
	effectiveOverdueInvoices,
	effectiveOverdueAmount,
	clientsWithOverdue,
}: {
	mode: KpiViewMode
	snapshot: NonNullSnapshot
	effectiveOverdueInvoices: number
	effectiveOverdueAmount: number
	clientsWithOverdue: number
}) {
	const totalOutstandingAmount = Number(snapshot.outstanding_amount || 0)
	const avgPaymentRate = Number(snapshot.month_payment_rate || 0)
	const pctOutstanding = Number(snapshot.overdue_percentage_of_outstanding || 0)
	const notYetDue = Number(snapshot.not_yet_due_amount || 0)

	const Stat: React.FC<{
		label: string
		value: React.ReactNode
		emphasis?: boolean
	}> = ({ label, value, emphasis }) => (
		<div className="flex flex-col gap-0.5 min-w-0">
			<span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</span>
			<span
				className={`tabular-nums truncate ${
					emphasis ? 'text-[18px] font-bold text-slate-900 leading-tight' : 'text-[13px] font-semibold text-slate-800'
				}`}
			>
				{value}
			</span>
		</div>
	)

	if (mode === 'overdue') {
		return (
			<div className="rounded-lg border border-rose-200/60 bg-gradient-to-r from-rose-50/90 via-white to-amber-50/40 px-4 py-3 flex flex-wrap items-end gap-x-10 gap-y-3">
				<Stat
					label="Overdue amount"
					value={<span className="text-rose-700">{formatCurrency(effectiveOverdueAmount)}</span>}
					emphasis
				/>
				<Stat label="Overdue invoices" value={effectiveOverdueInvoices} />
				<Stat label="Not yet due" value={formatCurrency(notYetDue)} />
				<div className="ml-auto text-[11px] text-slate-600 font-medium">
					<span className="text-slate-900">{clientsWithOverdue}</span> clients with overdue ·{' '}
					<span className="text-amber-800">{pctOutstanding.toFixed(1)}%</span> of outstanding
				</div>
			</div>
		)
	}

	if (mode === 'invoicing') {
		return (
			<div className="rounded-lg border border-sky-200/60 bg-gradient-to-r from-sky-50/90 via-white to-indigo-50/30 px-4 py-3 flex flex-wrap items-end gap-x-10 gap-y-3">
				<Stat
					label="Invoice amount"
					value={
						<span className="text-indigo-800">
							{formatCurrency(Number(snapshot.month_total_invoiced_amount || 0))}
						</span>
					}
					emphasis
				/>
				<Stat label="Total invoices" value={Number(snapshot.month_total_invoices || 0)} />
				<Stat
					label="Paid amount"
					value={formatCurrency(Number(snapshot.month_total_paid_amount || 0))}
				/>
				<div className="ml-auto text-[11px] text-slate-600 font-medium">
					<span className="text-slate-900">{Number(snapshot.active_clients || 0)}</span> active · avg{' '}
					<span className="text-sky-800">
						{formatCurrency(Number(snapshot.month_avg_invoice_size || 0))}
					</span>
					/inv
				</div>
			</div>
		)
	}

	return (
		<div className="rounded-lg border border-violet-200/60 bg-gradient-to-r from-violet-50/80 via-white to-emerald-50/30 px-4 py-3 flex flex-wrap items-end gap-x-10 gap-y-3">
			<Stat
				label="Outstanding"
				value={
					<span className="text-violet-900">
						{Number(snapshot.month_outstanding_invoices || 0)} · {formatCurrency(totalOutstandingAmount)}
					</span>
				}
				emphasis
			/>
			<Stat label="Payment rate" value={<span className="text-emerald-800">{avgPaymentRate.toFixed(1)}%</span>} />
			<Stat
				label="Collection rate"
				value={
					<span className="text-emerald-800">
						{Number(snapshot.period_collection_rate || 0).toFixed(1)}%
					</span>
				}
			/>
			<div className="ml-auto text-[11px] text-slate-500 font-medium">Paid vs invoiced · efficiency</div>
		</div>
	)
}

export const ClientDetailsTable: React.FC<ClientDetailsTableProps> = ({
	clients: rawClients,
	mode,
	onModeChange,
	snapshot,
	effectiveOverdueInvoices,
	effectiveOverdueAmount,
	clientsWithOverdue,
	showInvoicingView,
	showOnlyOverdueClients,
	showOutstandingOnly,
	showMultipleOverdueOnly,
	onMultipleOverdueOnlyChange,
	sort,
	onSort,
	onClientClick,
}) => {
	const filtered = useMemo(() => {
		let list = [...rawClients]
		if (!showInvoicingView && showOnlyOverdueClients) {
			list = list.filter(
				c =>
					(Number(c.overdue_invoice_count) || Number(c.overdue_invoices) || 0) > 0 ||
					Number(c.overdue_amount) > 0
			)
		}
		if (showMultipleOverdueOnly) {
			list = list.filter(c => Number(c.overdue_invoice_count || c.overdue_invoices) > 1)
		}
		if (!showInvoicingView && showOutstandingOnly) {
			list = list.filter(
				c =>
					Number(c.outstanding_invoices) > 0 ||
					Number(c.total_outstanding_amount || c.outstanding_amount) > 0
			)
		}
		return list
	}, [
		rawClients,
		showInvoicingView,
		showOnlyOverdueClients,
		showOutstandingOnly,
		showMultipleOverdueOnly,
	])

	const sorted = useMemo(() => {
		const key = sort.by
		return [...filtered].sort((a, b) => {
			const aVal = getSortValue(a, key, mode)
			const bVal = getSortValue(b, key, mode)
			if (key === 'client_name') {
				const dir = sort.dir === 'asc' ? 1 : -1
				return dir * (aVal < bVal ? -1 : aVal > bVal ? 1 : 0)
			}
			const dir = sort.dir === 'desc' ? 1 : -1
			return dir * (Number(bVal) - Number(aVal))
		})
	}, [filtered, sort, mode])

	const amountLabel =
		mode === 'invoicing'
			? 'Invoiced amount'
			: mode === 'outstanding'
				? 'Outstanding amount'
				: 'Overdue amount'

	return (
		<div className="bg-white rounded-xl border border-slate-200 shadow-md overflow-hidden">
			<div className="px-4 pt-3 pb-3 border-b border-slate-100 space-y-3">
				<div className="flex flex-wrap items-center justify-between gap-3">
					<div className="flex items-center gap-3 min-w-0">
						<span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 shrink-0">
							View
						</span>
						<div className="inline-flex rounded-lg bg-slate-100 p-0.5 gap-0.5">
							{VIEW_TABS.map(t => (
								<button
									key={t.id}
									type="button"
									onClick={() => onModeChange(t.id)}
									className={`px-3 py-1.5 rounded-md text-[12px] font-semibold transition-all whitespace-nowrap ${
										mode === t.id
											? 'bg-white text-slate-900 shadow-sm'
											: 'text-slate-500 hover:text-slate-700'
									}`}
								>
									{t.label}
								</button>
							))}
						</div>
					</div>
					<div className="flex flex-wrap items-center gap-2 justify-end">
						<FilterChip
							active={showMultipleOverdueOnly}
							label="Multiple overdue"
							onClick={() => onMultipleOverdueOnlyChange(!showMultipleOverdueOnly)}
						/>
						<span className="text-[11px] text-slate-500 tabular-nums pl-1">
							{sorted.length} clients
						</span>
					</div>
				</div>

				{snapshot ? (
					<ModeSummaryStrip
						mode={mode}
						snapshot={snapshot}
						effectiveOverdueInvoices={effectiveOverdueInvoices}
						effectiveOverdueAmount={effectiveOverdueAmount}
						clientsWithOverdue={clientsWithOverdue}
					/>
				) : (
					<div className="rounded-lg border border-dashed border-slate-200 bg-slate-50/50 px-4 py-3 text-center text-[12px] text-slate-500">
						No snapshot for current filters
					</div>
				)}
			</div>

			<div className="overflow-x-auto">
				<table className="w-full text-[12px]">
					<thead>
						<tr className="border-b border-slate-100 bg-slate-50">
							<ThSort field="client_name" label="Client" sort={sort} onSort={onSort} />
							<ThSort
								field="display_amount"
								label={amountLabel}
								sort={sort}
								onSort={onSort}
								align="right"
							/>
							{mode !== 'invoicing' && (
								<ThSort
									field="base_amount"
									label={mode === 'outstanding' ? 'Outstanding (base)' : 'Overdue (base)'}
									sort={sort}
									onSort={onSort}
									align="right"
								/>
							)}
							<ThSort
								field="overdue_count"
								label="Overdue #"
								sort={sort}
								onSort={onSort}
								align="right"
							/>
							<ThSort
								field="total_invoices"
								label="Total inv"
								sort={sort}
								onSort={onSort}
								align="right"
							/>
							<ThSort field="paid_invoices" label="Paid" sort={sort} onSort={onSort} align="right" />
							<ThSort
								field="outstanding_invoices"
								label="Outstanding"
								sort={sort}
								onSort={onSort}
								align="right"
							/>
							<ThSort
								field="payment_rate"
								label="Payment %"
								sort={sort}
								onSort={onSort}
								align="right"
							/>
							<ThSort
								field="avg_days_overdue"
								label="Avg days"
								sort={sort}
								onSort={onSort}
								align="right"
							/>
							<ThSort
								field="deposit_amount"
								label="Deposit"
								sort={sort}
								onSort={onSort}
								align="right"
							/>
							<ThSort
								field="deposit_adjustment"
								label="Dep. adj."
								sort={sort}
								onSort={onSort}
								align="right"
							/>
							<ThSort
								field="net_deposit_balance"
								label="Dep. balance"
								sort={sort}
								onSort={onSort}
								align="right"
							/>
						</tr>
					</thead>
					<tbody>
						{sorted.map((client, index) => {
							const name = String(client.contact_name || client.client_name || 'Unknown').trim()
							const rawId = client.contact_id
							const id: string | number =
								typeof rawId === 'string' || typeof rawId === 'number' ? rawId : name
							const baseCcy = String(client.base_currency || 'USD')
							const sym = getCurrencySymbol(baseCcy)
							const overdueN = Number(client.overdue_invoice_count || client.overdue_invoices || 0)
							const payRate = Number(client.payment_rate || 0)
							const avgDays = Number(client.avg_days_overdue || 0)
							const displayAmount = getSortValue(client, 'display_amount', mode) as number
							const baseAmount = getSortValue(client, 'base_amount', mode) as number

							return (
								<tr
									key={`${String(id)}-${name}-${index}`}
									className="border-b border-slate-50 hover:bg-slate-50/80"
								>
									<td className="px-2 py-2">
										<button
											type="button"
											onClick={() => onClientClick(id, name)}
											className="text-left font-medium text-blue-700 hover:underline"
										>
											{name}
										</button>
									</td>
									<td className="px-2 py-2 text-right font-semibold tabular-nums text-slate-900">
										{formatCurrency(displayAmount)}
									</td>
									{mode !== 'invoicing' && (
										<td className="px-2 py-2 text-right tabular-nums text-slate-800">
											<span className="font-semibold">
												{sym}
												{Number(baseAmount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
											</span>
										</td>
									)}
									<td
										className={`px-2 py-2 text-right tabular-nums ${overdueN > 0 ? 'text-rose-700 font-medium' : 'text-slate-400'}`}
									>
										{overdueN}
									</td>
									<td className="px-2 py-2 text-right tabular-nums text-sky-800">
										{Number(client.total_invoices || 0)}
									</td>
									<td className="px-2 py-2 text-right tabular-nums text-emerald-800">
										{Number(client.paid_invoices || 0)}
									</td>
									<td className="px-2 py-2 text-right tabular-nums text-amber-800">
										{Number(client.outstanding_invoices || 0)}
									</td>
									<td
										className={`px-2 py-2 text-right font-medium tabular-nums ${
											payRate >= 90
												? 'text-emerald-700'
												: payRate >= 70
													? 'text-amber-700'
													: 'text-rose-700'
										}`}
									>
										{payRate.toFixed(0)}%
									</td>
									<td className="px-2 py-2 text-right tabular-nums text-slate-700">
										{avgDays > 0 ? `${avgDays.toFixed(0)} d` : '—'}
									</td>
									<td className="px-2 py-2 text-right tabular-nums text-slate-700">
										{formatCurrency(Number(client.deposit_amount || 0))}
									</td>
									<td className="px-2 py-2 text-right tabular-nums text-slate-700">
										{formatCurrency(Number(client.deposit_adjustment || 0))}
									</td>
									<td className="px-2 py-2 text-right tabular-nums text-slate-700">
										{formatCurrency(Number(client.net_deposit_balance || 0))}
									</td>
								</tr>
							)
						})}
					</tbody>
				</table>
			</div>
		</div>
	)
}
